import { Save as SaveIcon, Send as SendIcon } from '@mui/icons-material';
import { Alert, Autocomplete, Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getEmployees from '@src/APIs/graphQl/Employee/getEmployees';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { mockMessageService } from '../services/MockMessageService';
import type { IMessageDraft } from '../types/IMessage';

export function ComposeTab() {
    const [draft, setDraft] = useState<IMessageDraft>({
        to: [],
        subject: '',
        content: '',
    });
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const employees = useSelector((state: AppState) => state.employees);
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);

    useEffect(() => {
        loadDraft();
        loadEmployees();
    }, []);

    const loadEmployees = async () => {
        if (employees.length === 0) {
            setLoading(true);
            try {
                await getEmployees();
            } catch (error) {
                console.error('Error loading employees:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const loadDraft = async () => {
        try {
            const savedDraft = await mockMessageService.getDraft();
            if (savedDraft) {
                setDraft(savedDraft);
            }
        } catch (error) {
            console.error('Error loading draft:', error);
        }
    };

    const saveDraft = async () => {
        try {
            await mockMessageService.saveDraft(draft);
        } catch (error) {
            console.error('Error saving draft:', error);
        }
    };

    const handleSend = async () => {
        if (!draft.to.length || !draft.subject.trim() || !draft.content.trim()) {
            setError(langStrings.messages.fillAllFields);
            return;
        }

        setSending(true);
        setError(null);

        try {
            await mockMessageService.sendMessage(draft);
            await mockMessageService.clearDraft();

            setDraft({
                to: [],
                subject: '',
                content: '',
            });

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            setError(langStrings.messages.errorSendingMessage);
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    const handleRecipientChange = (event: any, newValue: Employee[]) => {
        const updatedDraft = { ...draft, to: newValue };
        setDraft(updatedDraft);
        saveDraft();
    };

    const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedDraft = { ...draft, subject: event.target.value };
        setDraft(updatedDraft);
        saveDraft();
    };

    const handleContentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedDraft = { ...draft, content: event.target.value };
        setDraft(updatedDraft);
        saveDraft();
    };

    return (
        <Box sx={{ p: 2 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
                    {langStrings.messages.composeNewMessage}
                </Typography>

                <Stack spacing={3}>
                    {success && <Alert severity="success">{langStrings.messages.messageSentSuccessfully}</Alert>}

                    {error && <Alert severity="error">{error}</Alert>}

                    <Autocomplete
                        multiple
                        loading={loading}
                        options={employees}
                        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                        value={draft.to}
                        onChange={handleRecipientChange}
                        noOptionsText={
                            loading ? langStrings.messages.loadingEmployees : langStrings.messages.noEmployeesFound
                        }
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    variant="outlined"
                                    label={`${option.firstName} ${option.lastName}`}
                                    {...getTagProps({ index })}
                                    key={option.id}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={langStrings.messages.to}
                                placeholder={langStrings.messages.selectRecipients}
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />

                    <TextField
                        label={langStrings.messages.subject}
                        value={draft.subject}
                        onChange={handleSubjectChange}
                        variant="outlined"
                        fullWidth
                        placeholder={langStrings.messages.enterSubject}
                    />

                    <TextField
                        label={langStrings.messages.message}
                        value={draft.content}
                        onChange={handleContentChange}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={8}
                        placeholder={langStrings.messages.yourMessage}
                    />

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" startIcon={<SaveIcon />} onClick={saveDraft} disabled={sending}>
                            {langStrings.messages.saveDraft}
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={handleSend}
                            disabled={sending || !draft.to.length || !draft.subject.trim() || !draft.content.trim()}
                        >
                            {sending ? langStrings.messages.sending : langStrings.messages.send}
                        </Button>
                    </Box>
                </Stack>
            </Paper>
        </Box>
    );
}
